import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  Clock,
  Plus,
  RefreshCw,
  Search,
  Stethoscope,
  Trash2,
  UserRoundCheck,
  Users,
  X,
  XCircle,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "../auth/OAuthContext";
import { apiClient } from "../api/client";
import { OrderFlowShell } from "../components/OrderFlowShell";
import { AdminUserSummary, OrganizationRow, Role, UserStatus } from "../types";

const PRACTITIONER_ROLES: Role[] = ["Nurse", "Doctor"];
const ORG_TYPES: OrganizationRow["org_type"][] = [
  "HHA",
  "PHYSICIAN_PRACTICE",
  "DME",
  "HOSPICE",
  "OTHER",
];

type FilterTab = "all" | "Doctor" | "Nurse" | "Admin" | "pending_approval";

const TABS: Array<{ value: FilterTab; label: string }> = [
  { value: "all", label: "All Users" },
  { value: "Doctor", label: "Doctors" },
  { value: "Nurse", label: "Nurses" },
  { value: "Admin", label: "Admins" },
  { value: "pending_approval", label: "Pending" },
];

export function AdminUsers() {
  const onDashboard = useLocation().pathname === "/dashboard";
  const { idToken, email } = useAuth();
  const [organizations, setOrganizations] = useState<OrganizationRow[]>([]);
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterTab, setFilterTab] = useState<FilterTab>("all");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);

  const reload = useCallback(async () => {
    if (!idToken) return;
    setRefreshing(true);
    const client = apiClient(idToken);
    try {
      const [orgsRes, usersRes] = await Promise.all([
        client.listOrganizations(),
        client.listUsers(),
      ]);
      setOrganizations(orgsRes.organizations);
      setUsers(usersRes.users);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load admin data");
    } finally {
      setRefreshing(false);
    }
  }, [idToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const pendingUsers = useMemo(() => {
    return users.filter((user) => user.status === "pending_approval");
  }, [users]);

  const metrics = useMemo(() => {
    const total = users.length;
    const doctors = users.filter((u) => u.role === "Doctor").length;
    const nurses = users.filter((u) => u.role === "Nurse").length;
    const pending = pendingUsers.length;
    return { total, doctors, nurses, pending };
  }, [users, pendingUsers]);

  const visibleUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((user) => {
      const matchesTab =
        filterTab === "all"
          ? true
          : filterTab === "pending_approval"
          ? user.status === "pending_approval"
          : user.role === filterTab;

      if (!matchesTab) return false;

      if (!term) return true;

      const orgName = organizations.find((org) => org.id === user.organization_id)?.name ?? "";
      return [
        user.name,
        user.email,
        user.role,
        user.username,
        user.npi,
        user.status,
        orgName,
      ].some((value) => (value ?? "").toLowerCase().includes(term));
    });
  }, [organizations, search, users, filterTab]);

  const totalPages = Math.max(1, Math.ceil(visibleUsers.length / pageSize));
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return visibleUsers.slice(start, start + pageSize);
  }, [visibleUsers, currentPage, pageSize]);

  return (
    <OrderFlowShell
      title={onDashboard ? "Dashboard" : "User management"}
      subtitle="Manage organizations, practitioners, approvals, and system access."
      breadcrumb={onDashboard ? "/ Dashboard" : "/ User management"}
      actions={
        <button
          type="button"
          className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl transition-all shadow-2xs cursor-pointer disabled:opacity-50"
          onClick={() => void reload()}
          disabled={refreshing}
        >
          <RefreshCw size={15} className={`text-slate-500 ${refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
          <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
        </button>
      }
    >
      {loadError && (
        <div className="mb-6 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
          {loadError}
        </div>
      )}

      {/* Metric Cards (Clickable filter tabs) */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8" aria-label="User statistics">
        <div
          className={`p-5 rounded-2xl bg-white border transition-all cursor-pointer shadow-xs hover:shadow-md ${
            filterTab === "all"
              ? "border-[#012699] ring-2 ring-[#012699]/15"
              : "border-slate-200 hover:border-slate-300"
          }`}
          onClick={() => {
            setFilterTab("all");
            setCurrentPage(1);
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total users</span>
            <div className="w-9 h-9 rounded-xl bg-blue-50 text-[#012699] flex items-center justify-center">
              <Users size={20} aria-hidden="true" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-slate-900 font-display mt-2">{metrics.total}</div>
        </div>

        <div
          className={`p-5 rounded-2xl bg-white border transition-all cursor-pointer shadow-xs hover:shadow-md ${
            filterTab === "Doctor"
              ? "border-[#012699] ring-2 ring-[#012699]/15"
              : "border-slate-200 hover:border-slate-300"
          }`}
          onClick={() => {
            setFilterTab("Doctor");
            setCurrentPage(1);
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Doctors</span>
            <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Stethoscope size={20} aria-hidden="true" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-slate-900 font-display mt-2">{metrics.doctors}</div>
        </div>

        <div
          className={`p-5 rounded-2xl bg-white border transition-all cursor-pointer shadow-xs hover:shadow-md ${
            filterTab === "Nurse"
              ? "border-[#012699] ring-2 ring-[#012699]/15"
              : "border-slate-200 hover:border-slate-300"
          }`}
          onClick={() => {
            setFilterTab("Nurse");
            setCurrentPage(1);
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nurses</span>
            <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <UserRoundCheck size={20} aria-hidden="true" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-slate-900 font-display mt-2">{metrics.nurses}</div>
        </div>

        <div
          className={`p-5 rounded-2xl bg-white border transition-all cursor-pointer shadow-xs hover:shadow-md ${
            filterTab === "pending_approval"
              ? "border-[#012699] ring-2 ring-[#012699]/15"
              : "border-slate-200 hover:border-slate-300"
          }`}
          onClick={() => {
            setFilterTab("pending_approval");
            setCurrentPage(1);
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Pending approvals</span>
            <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
              <Clock size={20} aria-hidden="true" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-slate-900 font-display mt-2">{metrics.pending}</div>
        </div>
      </section>

      {/* Pending Nurse Approvals Section */}
      {pendingUsers.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 shadow-xs p-6 sm:p-8 mb-8 flex flex-col gap-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
            <div>
              <h2 className="text-lg font-bold text-slate-900 font-display">Pending nurse signups</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {pendingUsers.length} approval{pendingUsers.length === 1 ? "" : "s"} waiting for organization assignment and review
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 self-start sm:self-auto">
              <span className="w-2 h-2 rounded-full bg-amber-500" aria-hidden="true" />
              {pendingUsers.length} Pending
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <PendingApprovals
              users={pendingUsers}
              organizations={organizations}
              onDecided={reload}
            />
          </div>
        </section>
      )}

      {/* Create User Form Section */}
      <CreateUserForm
        organizations={organizations}
        onOrganizationCreated={reload}
        onCreated={reload}
      />

      {/* All Users Directory Table Section */}
      <div className="flex flex-col gap-4 mt-8">
        {/* Controls: Tabs & Search */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <div className="flex items-center gap-1.5 p-1 bg-slate-100/80 rounded-xl overflow-x-auto" role="tablist">
            {TABS.map((tab) => {
              const isActive = filterTab === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    isActive
                      ? "bg-white text-slate-900 shadow-xs"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                  }`}
                  onClick={() => {
                    setFilterTab(tab.value);
                    setCurrentPage(1);
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="relative w-full sm:w-72">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-xl placeholder:text-slate-400 focus:outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/10 transition-all"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search users, email, NPI..."
              aria-label="Search users"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <th className="py-3.5 px-6">Name</th>
                  <th className="py-3.5 px-6">Email & Phone</th>
                  <th className="py-3.5 px-6">Role</th>
                  <th className="py-3.5 px-6">Username</th>
                  <th className="py-3.5 px-6">Organization</th>
                  <th className="py-3.5 px-6">Status</th>
                  <th className="py-3.5 px-6 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-400 font-medium">
                      No users match the selected filter.
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user) => (
                    <UserTableRow
                      key={user.id}
                      user={user}
                      organizations={organizations}
                      currentEmail={email}
                      onDeleted={reload}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer: Rows per page & Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-3.5 border-t border-slate-100 bg-slate-50/40 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <span className="font-medium">Rows per page</span>
              <div className="relative">
                <select
                  className="appearance-none pl-3 pr-7 py-1 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-[#012699] cursor-pointer"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="font-medium">
                Page {currentPage} of <strong className="text-slate-900">{totalPages}</strong>
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <ArrowLeft size={14} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  <ArrowRight size={14} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OrderFlowShell>
  );
}

function CreateUserForm({
  organizations,
  onOrganizationCreated,
  onCreated,
}: {
  organizations: OrganizationRow[];
  onOrganizationCreated: () => void;
  onCreated: () => void;
}) {
  const { idToken } = useAuth();
  const [role, setRole] = useState<Role>("Nurse");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [npi, setNpi] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // New Organization Modal State
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgType, setNewOrgType] = useState<OrganizationRow["org_type"]>("PHYSICIAN_PRACTICE");
  const [newOrgNpi, setNewOrgNpi] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);

  const isPractitioner = PRACTITIONER_ROLES.includes(role);

  async function handleCreateOrg(e: FormEvent) {
    e.preventDefault();
    if (!idToken || !newOrgName.trim()) return;
    setCreatingOrg(true);
    setOrgError(null);
    try {
      const { organization } = await apiClient(idToken).createOrganization({
        name: newOrgName.trim(),
        orgType: newOrgType,
        npi: newOrgNpi.trim() || undefined,
      });
      setOrganizationId(organization.id);
      setNewOrgName("");
      setNewOrgNpi("");
      setShowOrgModal(false);
      onOrganizationCreated();
    } catch (err) {
      setOrgError(err instanceof Error ? err.message : "Failed to create organization");
    } finally {
      setCreatingOrg(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!idToken) return;
    setSubmitting(true);
    setStatus(null);
    setError(null);
    try {
      const { userId } = await apiClient(idToken).createUser({
        email: email.trim(),
        role,
        name: name.trim() || undefined,
        npi: isPractitioner ? npi.trim() : undefined,
        organizationId: isPractitioner ? organizationId : undefined,
        phone: phone.trim() || undefined,
      });
      setStatus(`Successfully created ${role} account (${userId}). Invitation email sent.`);
      setName("");
      setEmail("");
      setPhone("");
      setNpi("");
      setOrganizationId("");
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-6 sm:p-8 flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-bold text-slate-900 font-display">Create account</h2>
        <p className="text-xs text-slate-500">
          Invite a clinician, nurse, or administrator into MEATrix OrderFlow.
        </p>
      </div>

      {status && (
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium">
          {status}
        </div>
      )}
      {error && (
        <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700">Role</label>
          <div className="relative">
            <select
              className="w-full appearance-none min-h-[44px] px-3.5 pr-8 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all text-slate-900 cursor-pointer"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
            >
              <option value="Nurse">Nurse</option>
              <option value="Doctor">Doctor</option>
              <option value="Admin">Admin</option>
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700">Full name</label>
          <input
            type="text"
            className="w-full min-h-[44px] px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all text-slate-900 placeholder:text-slate-400"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Dr. Emily Johnson"
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700">Email address</label>
          <input
            type="email"
            className="w-full min-h-[44px] px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all text-slate-900 placeholder:text-slate-400"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="practitioner@example.com"
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-slate-700">Phone number</label>
          <input
            type="tel"
            className="w-full min-h-[44px] px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all text-slate-900 placeholder:text-slate-400"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 000-0000"
          />
        </div>

        {isPractitioner && (
          <>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">10-Digit NPI</label>
              <input
                type="text"
                inputMode="numeric"
                className="w-full min-h-[44px] px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all text-slate-900 placeholder:text-slate-400"
                value={npi}
                onChange={(e) => setNpi(e.target.value.replace(/\D/g, "").slice(0, 10))}
                pattern="\d{10}"
                placeholder="10-Digit NPI"
                title="Must be exactly 10 digits"
                required
              />
            </div>

            <div className="flex flex-col gap-1.5 sm:col-span-2 lg:col-span-1">
              <label className="text-xs font-bold text-slate-700">Practice / Organization</label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <select
                    className="w-full appearance-none min-h-[44px] px-3.5 pr-8 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all text-slate-900 cursor-pointer"
                    value={organizationId}
                    onChange={(e) => setOrganizationId(e.target.value)}
                    required
                  >
                    <option value="">Select organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name} ({org.org_type})
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                </div>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-xl text-xs font-bold text-[#012699] bg-[#012699]/10 hover:bg-[#012699] hover:text-white transition-all shrink-0 cursor-pointer"
                  onClick={() => setShowOrgModal(true)}
                  title="Add new organization"
                >
                  <Plus size={16} aria-hidden="true" />
                  <span>Add Org</span>
                </button>
              </div>
            </div>
          </>
        )}

        <div className="col-span-1 sm:col-span-2 lg:col-span-3 pt-2">
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-6 min-h-[46px] rounded-xl font-bold text-sm text-white bg-[#012699] hover:bg-[#0b2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm cursor-pointer"
            disabled={submitting}
          >
            <Plus size={18} aria-hidden="true" />
            <span>{submitting ? "Creating account..." : `Create ${role} account`}</span>
          </button>
        </div>
      </form>

      {/* Add Organization Modal */}
      {showOrgModal && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setShowOrgModal(false)}
        >
          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 p-6 flex flex-col gap-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#012699] flex items-center justify-center">
                  <Building2 size={18} aria-hidden="true" />
                </div>
                <h3 className="text-base font-bold text-slate-900 font-display">Add organization</h3>
              </div>
              <button
                type="button"
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                onClick={() => setShowOrgModal(false)}
                aria-label="Close"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            {orgError && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium">
                {orgError}
              </div>
            )}

            <form onSubmit={handleCreateOrg} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-700">Organization name</label>
                <input
                  type="text"
                  className="w-full min-h-[44px] px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all text-slate-900 placeholder:text-slate-400"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="e.g. Metro Health Home Care"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-700">Organization type</label>
                <div className="relative">
                  <select
                    className="w-full appearance-none min-h-[44px] px-3.5 pr-8 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all text-slate-900 cursor-pointer"
                    value={newOrgType}
                    onChange={(e) => setNewOrgType(e.target.value as OrganizationRow["org_type"])}
                  >
                    {ORG_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-slate-700">Organization NPI (optional)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-full min-h-[44px] px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all text-slate-900 placeholder:text-slate-400"
                  value={newOrgNpi}
                  onChange={(e) => setNewOrgNpi(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-Digit Organization NPI"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                  onClick={() => setShowOrgModal(false)}
                  disabled={creatingOrg}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-xs font-bold text-white bg-[#012699] hover:bg-[#0b2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all shadow-xs cursor-pointer"
                  disabled={creatingOrg || !newOrgName.trim()}
                >
                  {creatingOrg ? "Creating..." : "Save organization"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function PendingApprovals({
  users,
  organizations,
  onDecided,
}: {
  users: AdminUserSummary[];
  organizations: OrganizationRow[];
  onDecided: () => void;
}) {
  const { idToken } = useAuth();
  const [selectedOrg, setSelectedOrg] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function approve(userId: string) {
    if (!idToken) return;
    const organizationId = selectedOrg[userId];
    if (!organizationId) return;
    setBusy(userId);
    setError(null);
    try {
      await apiClient(idToken).approveUser(userId, { organizationId });
      onDecided();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve user");
    } finally {
      setBusy(null);
    }
  }

  async function reject(userId: string) {
    if (!idToken) return;
    setBusy(userId);
    setError(null);
    try {
      await apiClient(idToken).rejectUser(userId);
      onDecided();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject user");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {error && (
        <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium">
          {error}
        </div>
      )}
      {users.map((user) => (
        <div
          className="p-4 rounded-xl border border-slate-200 hover:border-slate-300 bg-slate-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4 transition-all"
          key={user.id}
        >
          <div className="flex items-start gap-3.5">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm shrink-0">
              {(user.name?.[0] || user.email?.[0] || "U").toUpperCase()}
            </div>
            <div className="flex flex-col gap-0.5 min-w-0">
              <strong className="text-sm font-bold text-slate-900 truncate">
                {user.name ?? user.email}
              </strong>
              <span className="text-xs text-slate-500">
                {user.email} • NPI: {user.npi ?? "N/A"} • Username: {user.username ?? "N/A"}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative min-w-[200px]">
              <select
                className="w-full appearance-none min-h-[38px] pl-3 pr-8 py-1.5 text-xs bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] text-slate-900 cursor-pointer"
                value={selectedOrg[user.id] ?? ""}
                onChange={(e) => setSelectedOrg((curr) => ({ ...curr, [user.id]: e.target.value }))}
              >
                <option value="">Assign organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.org_type})
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
            </div>

            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-2xs cursor-pointer"
              onClick={() => void approve(user.id)}
              disabled={busy === user.id || !selectedOrg[user.id]}
            >
              <CheckCircle2 size={15} aria-hidden="true" />
              <span>{busy === user.id ? "Approving..." : "Approve"}</span>
            </button>

            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-all cursor-pointer disabled:opacity-50"
              onClick={() => void reject(user.id)}
              disabled={busy === user.id}
            >
              <XCircle size={15} aria-hidden="true" />
              <span>Reject</span>
            </button>
          </div>
        </div>
      ))}
    </>
  );
}

function UserTableRow({
  user,
  organizations,
  currentEmail,
  onDeleted,
}: {
  user: AdminUserSummary;
  organizations: OrganizationRow[];
  currentEmail: string | null;
  onDeleted: () => void;
}) {
  const { idToken } = useAuth();
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  async function deleteUser(targetUser: AdminUserSummary) {
    if (!idToken) return;
    const confirmed = window.confirm(`Are you sure you want to delete ${targetUser.email}?`);
    if (!confirmed) return;

    setBusyUserId(targetUser.id);
    try {
      await apiClient(idToken).deleteUser(targetUser.id);
      onDeleted();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete user");
    } finally {
      setBusyUserId(null);
    }
  }

  const orgName = organizations.find((org) => org.id === user.organization_id)?.name ?? "—";

  return (
    <tr className="hover:bg-slate-50/60 transition-colors">
      <td className="py-4 px-6">
        <div className="font-bold text-slate-900 leading-snug">{user.name || "—"}</div>
        {user.npi && <div className="text-xs text-slate-500 mt-0.5">NPI: {user.npi}</div>}
      </td>
      <td className="py-4 px-6">
        <span className="font-medium text-slate-900 leading-tight block">{user.email}</span>
        {user.phone && <div className="text-xs text-slate-400 mt-0.5">{user.phone}</div>}
      </td>
      <td className="py-4 px-6 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
            user.role === "Doctor"
              ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
              : user.role === "Nurse"
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-purple-50 text-purple-700 border border-purple-200"
          }`}
        >
          {user.role}
        </span>
      </td>
      <td className="py-4 px-6 font-mono text-xs text-slate-600 whitespace-nowrap">
        {user.username ?? "—"}
      </td>
      <td className="py-4 px-6 whitespace-nowrap">
        <span className="font-medium text-slate-700">{orgName}</span>
      </td>
      <td className="py-4 px-6 whitespace-nowrap">
        <UserStatusChip status={user.status} />
      </td>
      <td className="py-4 px-6 text-right whitespace-nowrap">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={() => void deleteUser(user)}
          disabled={busyUserId === user.id || user.email === currentEmail}
          title={user.email === currentEmail ? "Cannot delete own account" : "Delete user"}
        >
          <Trash2 size={14} aria-hidden="true" />
          <span>{busyUserId === user.id ? "Deleting..." : "Delete"}</span>
        </button>
      </td>
    </tr>
  );
}

function UserStatusChip({ status }: { status: UserStatus }) {
  switch (status) {
    case "active":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
          Active
        </span>
      );
    case "pending_approval":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
          Pending
        </span>
      );
    case "invited":
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-[#012699] border border-blue-200">
          <span className="w-1.5 h-1.5 rounded-full bg-[#012699]" aria-hidden="true" />
          Invited
        </span>
      );
    case "disabled":
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" aria-hidden="true" />
          Disabled
        </span>
      );
  }
}
